/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
  TOKEN_EXPIRED_OR_INVALID_MESSAGE,
} from "../../utils/apiConstants.js";
/**
 * @fileoverview User logic module.
 * Handles user data retrieval, filtering, sorting, and pagination operations.
 * @module logic/user
 */

import {
  getToken,
  refreshToken,
  signOut,
  CAINTRA_RFCS,
} from "../../utils/common.js";
import { getAuthJsonHeaders } from "../../utils/eventApi.js";
import { logger, sanitizeError } from "../../utils/logger.js";

/** Role IDs to exclude from the user-type dropdown when edited user's RFC is not in CAINTRA_RFCS. */
const ROLE_IDS_EXCLUDED_FOR_NON_CAINTRA = ["5", "8", "9", "10", "12"];
const HTTP_OK = 200;
const HTTP_CONFLICT = 409;

/** Default response when the Users page-data endpoint fails or is unavailable. */
const USERS_PAGE_DEFAULT_RESPONSE = {
  users: [],
  companies: {},
  lastEvaluatedKey: null,
};

/** Default response when the Users search endpoint fails or is unavailable. */
const USERS_SEARCH_DEFAULT_RESPONSE = {
  users: [],
  companies: {},
  searchType: null,
};

const USER_PAGE_DATA_SIZE = 1000;

/**
 * Parses a GET /user/page-data JSON body into the Users page shape.
 * @param {Object} data - Parsed response body
 * @param {number} status - HTTP status code
 * @returns {{ users: Array, companies: Object, lastEvaluatedKey: *|null }}
 */
const parseUsersPageData = (data, status) => {
  if (status !== HTTP_OK) {
    logger.error("Error fetching users page data:", {
      data: data?.message ?? status,
    });
    return USERS_PAGE_DEFAULT_RESPONSE;
  }

  return {
    users: Array.isArray(data.users) ? data.users : [],
    companies:
      data.companies && typeof data.companies === "object"
        ? data.companies
        : {},
    lastEvaluatedKey: data.lastEvaluatedKey ?? null,
  };
};

/**
 * Fetches the first page of data for the Users page (role-scoped via GET /user/page-data/{userId}).
 * Admins receive all users; primary users receive themselves plus secondary accounts; other roles
 * receive only their own profile.
 *
 * @async
 * @param {string} userId - The logged-in user's unique identifier
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @returns {Promise<{ users: Array, companies: Object, lastEvaluatedKey: *|null }>}
 */
export const initializeUsersPage = async (userId, navigate) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}user/page-data/${userId}?limit=${USER_PAGE_DATA_SIZE}`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        if (navigate) {
          navigate("/");
        }
        return USERS_PAGE_DEFAULT_RESPONSE;
      }
      return await initializeUsersPage(userId, navigate);
    }

    return parseUsersPageData(data, response.status);
  } catch (error) {
    logger.error("Error initializing Users page:", {
      err: sanitizeError(error),
    });
    return USERS_PAGE_DEFAULT_RESPONSE;
  }
};

/**
 * Fetches the next page of users and companies using the last evaluated key from GET /user/page-data/{userId}.
 *
 * @async
 * @param {string} userId - The logged-in user's unique identifier
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @param {string|Object} lastKey - Last evaluated key from the previous page response
 * @returns {Promise<{ users: Array, companies: Object, lastEvaluatedKey: *|null }>}
 */
export const fetchMoreUsersPage = async (userId, navigate, lastKey) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    // Backend returns lastEvaluatedKey already URL-encoded; send as-is to avoid double-encoding.
    const keyParam =
      typeof lastKey === "string"
        ? lastKey
        : encodeURIComponent(JSON.stringify(lastKey));
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}user/page-data/${userId}?limit=${USER_PAGE_DATA_SIZE}&lastKey=${keyParam}`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        if (navigate) {
          navigate("/");
        }
        return USERS_PAGE_DEFAULT_RESPONSE;
      }
      return await fetchMoreUsersPage(userId, navigate, lastKey);
    }

    return parseUsersPageData(data, response.status);
  } catch (error) {
    logger.error("Error fetching more users:", { err: sanitizeError(error) });
    return USERS_PAGE_DEFAULT_RESPONSE;
  }
};

/**
 * Parses a GET /user/search JSON body into the Users search shape.
 * @param {Object} data - Parsed response body
 * @param {number} status - HTTP status code
 * @returns {{ users: Array, companies: Object, searchType: string|null }}
 */
const parseUsersSearchData = (data, status) => {
  if (status !== HTTP_OK) {
    logger.error("Error searching users:", { data: data?.message ?? status });
    return USERS_SEARCH_DEFAULT_RESPONSE;
  }

  return {
    users: Array.isArray(data.users) ? data.users : [],
    companies:
      data.companies && typeof data.companies === "object"
        ? data.companies
        : {},
    searchType: data.searchType ?? null,
  };
};

/**
 * Searches users via GET /user/search/{userId}. The backend classifies the query as
 * email, RFC, or nombre and returns all matching users scoped to the requester's role.
 *
 * @async
 * @param {string} userId - The logged-in user's unique identifier
 * @param {string} query - Search string (nombre, RFC, or correoElectronico)
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @returns {Promise<{ users: Array, companies: Object, searchType: string|null }>}
 */
export const searchUsers = async (userId, query, navigate) => {
  const trimmedQuery = query?.trim?.() ?? "";
  if (!trimmedQuery) {
    return USERS_SEARCH_DEFAULT_RESPONSE;
  }

  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}user/search/${userId}?query=${encodeURIComponent(trimmedQuery)}`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        if (navigate) {
          navigate("/");
        }
        return USERS_SEARCH_DEFAULT_RESPONSE;
      }
      return await searchUsers(userId, query, navigate);
    }

    return parseUsersSearchData(data, response.status);
  } catch (error) {
    logger.error("Error searching users:", { err: sanitizeError(error) });
    return USERS_SEARCH_DEFAULT_RESPONSE;
  }
};

/**
 * Fetches role and permission data for a specific user.
 * @async
 * @param {string} userId - The user's unique identifier
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @returns {Promise<Object|null>} User permissions or null on error
 */
export const getUserRoleData = async (userId, navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}permission/${userId}`,
      options,
    );
    const roleData = await response.json();

    const isTokenExpired =
      roleData?.message === TOKEN_EXPIRED_MESSAGE ||
      roleData?.message === TOKEN_EXPIRED_OR_INVALID_MESSAGE;
    if (isTokenExpired) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await getUserRoleData(userId, navigate);
    }
    if (roleData?.message) {
      return null;
    }
    return roleData?.permisos ?? null;
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    return null;
  }
};

/**
 * Fetches all users from the API.
 * @async
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @returns {Promise<Array>} Array of user objects or empty array on error
 */
export const getUsers = async (navigate) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}user`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getUsers(navigate);
    }
    if (!response.ok || !Array.isArray(data)) {
      return [];
    }
    return data;
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    return [];
  }
};

/**
 * Fetches a single user by their identifier.
 * @async
 * @param {string} userId - The user's unique identifier
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @returns {Promise<Object|null>} User data or null on error
 */
export const getUser = async (userId, navigate) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}user/${userId}`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await getUser(userId, navigate);
    }
    if (!response.ok || data?.code === "FORBIDDEN") {
      return null;
    }
    return data;
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    return null;
  }
};

/**
 * Sorts an array of users by name according to the sort configuration.
 * @param {Array<Object>} users - Array of user objects to sort
 * @param {Object} sortConfig - Sort configuration object
 * @param {string} sortConfig.direction - Sort direction ('ascending' or 'descending')
 * @returns {Array<Object>} Sorted array of users
 */
export const sortUsers = (users, sortConfig) => {
  if (!Array.isArray(users) || users.length === 0) {
    return [];
  }

  return [...users].sort((a, b) => {
    const nameA = a.nombre ? a.nombre.toLowerCase() : "";
    const nameB = b.nombre ? b.nombre.toLowerCase() : "";
    if (nameA < nameB) {
      return sortConfig.direction === "ascending" ? -1 : 1;
    }
    if (nameA > nameB) {
      return sortConfig.direction === "ascending" ? 1 : -1;
    }
    return 0;
  });
};

/**
 * Filters users by name based on a search query.
 * @param {Array<Object>} users - Array of user objects to filter
 * @param {string} searchQuery - Search string to filter by
 * @returns {Array<Object>} Filtered array of users matching the search query
 */
export const filterUsers = (users, searchQuery) => {
  if (!searchQuery) {
    return users;
  }
  return users.filter(
    (user) =>
      user.nombre?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false,
  );
};

/**
 * Paginates an array of users for display.
 * @param {Array<Object>} users - Array of user objects to paginate
 * @param {number} currentPage - Current page number (1-indexed)
 * @param {number} resultsLimit - Number of results per page
 * @returns {Array<Object>} Subset of users for the current page
 */
export const paginateUsers = (users, currentPage, resultsLimit) => {
  return users.slice(
    (currentPage - 1) * resultsLimit,
    currentPage * resultsLimit,
  );
};

/**
 * Fetches all active users from a specific company.
 * @async
 * @param {string} companyRFC - The company's RFC identifier
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @returns {Promise<Array>} Array of active user objects or empty array on error
 */
export const getActiveUsersFromCompany = async (companyRFC, navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}user/company/${companyRFC}/active`,
      options,
    );
    const result = await response.json();

    if (result?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getActiveUsersFromCompany(companyRFC, navigate);
    }

    return result.data;
  } catch (error) {
    logger.error("Error fetching active users from company:", {
      err: sanitizeError(error),
    });
    return [];
  }
};

/**
 * Fetches active users from a company, excluding the current user.
 * @async
 * @param {string} companyRFC - The company's RFC identifier
 * @param {string} currentUserId - The current user's ID to exclude from results
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @returns {Promise<Array>} Array of active users excluding the current user
 */
export const getFilteredActiveUsersFromCompany = async (
  companyRFC,
  currentUserId,
  navigate,
) => {
  const activeUsers = await getActiveUsersFromCompany(companyRFC, navigate);

  // Filter out the current user from the results
  return activeUsers.filter((user) => user._id !== currentUserId);
};

/**
 * Fetches available user roles (permissions) from the API for the user type dropdown.
 * Handles token expiry with refresh and redirect.
 *
 * @async
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @returns {Promise<Array<{ value: string, label: string }>>} Roles for the dropdown or empty array on error
 */
export const getUserRoles = async (navigate) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}permission`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        if (navigate) {
          navigate("/");
        }
        return [];
      }
      return await getUserRoles(navigate);
    }
    if (!response.ok || !Array.isArray(data)) {
      return [];
    }
    return data.map((role) => ({
      value: role._id,
      label: role.nombre,
    }));
  } catch (error) {
    logger.error("Error fetching user roles:", { err: sanitizeError(error) });
    return [];
  }
};

/**
 * Roles the signed-in user may assign when creating a user.
 * Returns `{ value, label }` only. Does not load the admin permission catalog.
 *
 * @async
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @returns {Promise<Array<{ value: string, label: string }>>}
 */
export const getCreatableRoles = async (navigate) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}user/creatable-roles`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        if (navigate) {
          navigate("/");
        }
        return [];
      }
      return await getCreatableRoles(navigate);
    }
    if (!response.ok || !Array.isArray(data)) {
      return [];
    }
    return data
      .filter((role) => role?.value != null && role.value !== "")
      .map((role) => ({
        value: String(role.value),
        label: role.label ?? "",
      }));
  } catch (error) {
    logger.error("Error fetching creatable roles:", {
      err: sanitizeError(error),
    });
    return [];
  }
};

/**
 * Creates a user via API. Handles token expiry with refresh and retry.
 * On success calls onSuccess with the created user (including nombreRol).
 * On conflict sets RFC or email error via callbacks.
 *
 * @async
 * @param {Object} userData - User payload (tipoDeUsuario, nombre, titulo, correoElectronico, etc.)
 * @param {Array<{ value: string, label: string }>} roles - Roles list for resolving nombreRol
 * @param {Object} callbacks
 * @param {Function} callbacks.navigate - React Router navigation function
 * @param {Function} callbacks.onSuccess - Called with created user (with nombreRol)
 * @param {Function} callbacks.onRFCError - Called when company RFC not found
 * @param {Function} callbacks.onEmailError - Called when email already exists (409)
 * @param {Function} [callbacks.onError] - Called with the error body for other failures
 * @returns {Promise<{ success: boolean }?>} success true when user was created; undefined on redirect/error
 */
export const createUser = async (
  userData,
  roles,
  { navigate, onSuccess, onRFCError, onEmailError, onError },
) => {
  const authToken = getToken();
  if (!authToken) {
    logger.error("Failed to get the token");
    return { success: false };
  }

  const options = {
    method: "POST",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify(userData),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}user`,
      options,
    );

    if (response.ok) {
      const responseData = await response.json();
      const newUser = responseData.user;
      newUser.nombreRol =
        roles.find((role) => role.value === newUser.tipoDeUsuario)?.label ?? "";
      onSuccess(newUser);
      return { success: true };
    }

    const errorData = await response.json();
    if (errorData?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        if (navigate) {
          navigate("/");
        }
        return { success: false };
      }
      return await createUser(userData, roles, {
        navigate,
        onSuccess,
        onRFCError,
        onEmailError,
        onError,
      });
    }

    if (errorData?.errorType === "COMPANY_RFC_NOT_FOUND") {
      onRFCError();
    } else if (
      response.status === HTTP_CONFLICT &&
      errorData?.errorType === "EMAIL_ALREADY_EXISTS"
    ) {
      onEmailError();
    } else {
      onError?.(errorData);
    }
    return { success: false };
  } catch (error) {
    logger.error("Error:", { err: sanitizeError(error) });
    return { success: false };
  }
};

/**
 * Updates a user via API. Handles token expiry with refresh and retry.
 * On success calls onSuccess with the updated user; on 409 EMAIL_ALREADY_EXISTS calls onEmailError.
 *
 * @async
 * @param {string} userId - The user's unique identifier
 * @param {Object} userData - User payload (tipoDeUsuario, nombre, titulo, correoElectronico, etc.)
 * @param {Object} callbacks
 * @param {Function} callbacks.navigate - React Router navigation function
 * @param {Function} callbacks.onSuccess - Called with updated user
 * @param {Function} [callbacks.onEmailError] - Called when email already exists (409)
 * @param {Function} [callbacks.onError] - Called with errorData for any non-ok response (e.g. to show toast)
 * @returns {Promise<{ success: boolean }?>} success true when user was updated; undefined on redirect/error
 */
export const updateUser = async (
  userId,
  userData,
  { navigate, onSuccess, onEmailError, onError },
) => {
  const authToken = getToken();
  if (!authToken) {
    logger.error("Failed to get the token");
    return { success: false };
  }

  const options = {
    method: "PUT",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify(userData),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}user/${userId}`,
      options,
    );

    if (response.ok) {
      const updatedUser = await response.json();
      onSuccess(updatedUser);
      return { success: true };
    }

    const errorData = await response.json();
    if (errorData?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        if (navigate) {
          navigate("/");
        }
        return { success: false };
      }
      return await updateUser(userId, userData, {
        navigate,
        onSuccess,
        onEmailError,
        onError,
      });
    }

    if (
      response.status === HTTP_CONFLICT &&
      errorData?.errorType === "EMAIL_ALREADY_EXISTS"
    ) {
      onEmailError?.();
    }
    onError?.(errorData);
    return { success: false };
  } catch (error) {
    logger.error("Error:", { err: sanitizeError(error) });
    return { success: false };
  }
};

/**
 * Derives what the current user is allowed to edit when editing another user (or self).
 * - tipoDeUsuario 5 (Administrador): edit all fields of all users, including correoElectronico and tipoDeUsuario.
 * - tipoDeUsuario 2 (Socio primario): edit all fields of all users except correoElectronico and tipoDeUsuario.
 * - tipoDeUsuario 8, 9 or 12 with viewUsers permission: edit all users but only nombre, titulo, numeroCelular.
 * - Otherwise: can only edit self, and only nombre, titulo, numeroCelular.
 *
 * @param {Object|null} currentUser - The logged-in user (with tipoDeUsuario, _id).
 * @param {Array|null} permissions - Permissions array from AppContext (e.g. [{ viewUsers: true }]).
 * @returns {{ canEditAllExceptEmail: boolean, canEditEmail: boolean, canEditLimitedFields: boolean, canEditOnlySelf: boolean, canEditTipoDeUsuario: boolean }}
 */
export const getUserIdEditCapabilities = (currentUser, permissions) => {
  const tipo =
    currentUser?.tipoDeUsuario != null ? String(currentUser.tipoDeUsuario) : "";
  const hasViewUsers = Boolean(
    permissions?.find((p) =>
      Object.prototype.hasOwnProperty.call(p, "viewUsers"),
    )?.viewUsers,
  );

  if (tipo === "5") {
    return {
      canEditAllExceptEmail: true,
      canEditEmail: true,
      canEditLimitedFields: false,
      canEditOnlySelf: false,
      canEditTipoDeUsuario: true,
    };
  }
  if (tipo === "2") {
    return {
      canEditAllExceptEmail: true,
      canEditEmail: false,
      canEditLimitedFields: false,
      canEditOnlySelf: false,
      canEditTipoDeUsuario: false,
    };
  }
  if ((tipo === "8" || tipo === "9" || tipo === "12") && hasViewUsers) {
    return {
      canEditAllExceptEmail: false,
      canEditEmail: false,
      canEditLimitedFields: true,
      canEditOnlySelf: false,
      canEditTipoDeUsuario: false,
    };
  }
  return {
    canEditAllExceptEmail: false,
    canEditEmail: false,
    canEditLimitedFields: false,
    canEditOnlySelf: true,
    canEditTipoDeUsuario: false,
  };
};

/**
 * Filters the roles list for the user-type dropdown when editing a user.
 * If the current user can edit tipoDeUsuario and the selected user's RFC is not in CAINTRA_RFCS,
 * excludes roles with _id 5, 8, 9, 10, 12.
 *
 * @param {Array<{ value: string, label: string }>} roles - Full roles list (value = _id).
 * @param {boolean} canEditTipoDeUsuario - Whether the current user can change the edited user's role.
 * @param {string} [selectedUserRfc] - RFC of the user being edited.
 * @returns {Array<{ value: string, label: string }>} Filtered roles.
 */
export const filterUserRolesForEdit = (
  roles,
  canEditTipoDeUsuario,
  selectedUserRfc,
) => {
  if (!Array.isArray(roles)) {
    return [];
  }
  if (!canEditTipoDeUsuario) {
    return roles;
  }
  const rfc = selectedUserRfc?.trim?.();
  if (!rfc || CAINTRA_RFCS.includes(rfc)) {
    return roles;
  }
  return roles.filter(
    (r) => !ROLE_IDS_EXCLUDED_FOR_NON_CAINTRA.includes(String(r.value)),
  );
};

export const getUsersFromSpecificRole = async (roleId) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}user/role/${roleId}`,
      options,
    );
    const data = await response.json();
    if (!response.ok || !Array.isArray(data)) {
      return [];
    }
    return data;
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    return [];
  }
};
